"use client";

import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import { CustomCursor } from "./@components";
import CSVViewer from "./pages/smol/CSVViewer";

const Home = lazy(() => import("./pages/Home"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./@components/blogpost"));
const WhatWeDo = lazy(() => import("./pages/whatwedo"));
const CustomSolutions = lazy(() => import("./pages/custom-solutions"));
const ProductsPage = lazy(() => import("./pages/products/ProductsPage"));
const ProductRouter = lazy(() => import("./pages/products/ProductRouter"));
const About = lazy(() => import("./pages/about"));
const AuthorDanyalJamil = lazy(() => import("./pages/authors/danyal-jamil"));
const GulfDigitalMarketingStatistics = lazy(() =>
  import("./pages/research/GulfDigitalMarketingStatistics")
);
const ContactUs = lazy(() => import("./pages/contact-us"));
const Privacy = lazy(() => import("./pages/privacy"));
const Terms = lazy(() => import("./pages/terms"));
const DTAASPage = lazy(() => import("./pages/dtaas"));
const CaseStudiesPage = lazy(() => import("./pages/case-studies/page"));
const CaseStudyRouter = lazy(() =>
  import("./pages/case-studies/CaseStudyRouter")
);
const ServiceRouter = lazy(() => import("./pages/services/ServiceRouter"));
const AIinKuwait = lazy(() => import("./pages/AIinKuwait"));
const CardPage = lazy(() => import("./pages/card"));
const AdBudgetCalculator = lazy(() =>
  import("./pages/tools/AdBudgetCalculator")
);
const ScrollWorld = lazy(() =>
  import("./pages/case-studies/front-end/threejs-unleashed/ScrollWorld")
);
const MaterialLab = lazy(() =>
  import("./pages/case-studies/front-end/threejs-unleashed/MaterialLab")
);
const TypographicGravity = lazy(() =>
  import("./pages/case-studies/front-end/threejs-unleashed/TypographicGravity")
);
const NotFound = lazy(() => import("./pages/NotFound"));

const routeFallback = (
  <div
    style={{
      minHeight: "100vh",
      background: "#f2ecdf",
      color: "#8b8172",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "0.75rem",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
    }}
  >
    Loading…
  </div>
);

const App = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      setIsMobile(isTouchDevice || window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <Suspense fallback={routeFallback}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/what-we-do" element={<WhatWeDo />} />
            <Route path="/custom-solutions" element={<CustomSolutions />} />
            <Route path="/products/:slug" element={<ProductRouter />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route
              path="/specialties/*"
              element={<Navigate to="/products" replace />}
            />
            <Route path="/about" element={<About />} />
            <Route
              path="/authors/danyal-jamil"
              element={<AuthorDanyalJamil />}
            />
            <Route
              path="/research/gulf-digital-marketing-statistics"
              element={<GulfDigitalMarketingStatistics />}
            />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route
              path="/contact"
              element={<Navigate to="/contact-us" replace />}
            />
            <Route path="/ai-in-kuwait" element={<AIinKuwait />} />
            <Route path="/dtaas" element={<DTAASPage />} />

            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/page/:page" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/services/:slug" element={<ServiceRouter />} />

            <Route path="/csvviewer" element={<CSVViewer />} />
            {/* <Route path="/fedev" element={<Fedev />} /> */}
            <Route
              path="/casestudy"
              element={<Navigate to="/case-studies" replace />}
            />

            <Route path="/card" element={<CardPage />} />
            <Route
              path="/tools/ad-budget-calculator"
              element={<AdBudgetCalculator />}
            />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route
              path="/case-studies/front-end/threejs-unleashed/scroll"
              element={<ScrollWorld />}
            />
            <Route
              path="/case-studies/front-end/threejs-unleashed/lab"
              element={<MaterialLab />}
            />
            <Route
              path="/case-studies/front-end/threejs-unleashed/type"
              element={<TypographicGravity />}
            />
            <Route
              path="/case-studies/front-end/threejs-unleashed/dreamscape"
              element={
                <Navigate
                  to="/case-studies/front-end/threejs-unleashed/type"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/ai/miracle-model-ai"
              element={
                <Navigate
                  to="/case-studies/product-engineering/shakl"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/product-engineering/miracle-model-ai"
              element={
                <Navigate
                  to="/case-studies/product-engineering/shakl"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/ai/spying-agent"
              element={
                <Navigate
                  to="/case-studies/product-engineering/spying-agent"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/front-end/kitty-care"
              element={
                <Navigate
                  to="/case-studies/product-engineering/kitty-care"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/front-end/ilmora"
              element={
                <Navigate
                  to="/case-studies/product-engineering/ilmora"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/front-end/mehfil-media"
              element={
                <Navigate
                  to="/case-studies/product-engineering/mehfil-media"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/front-end/manzil-delivery-platform"
              element={
                <Navigate
                  to="/case-studies/product-engineering/manzil-delivery-platform"
                  replace
                />
              }
            />
            <Route
              path="/case-studies/:category/:slug"
              element={<CaseStudyRouter />}
            />

            <Route
              path="/showcase/*"
              element={
                <Navigate
                  to="/case-studies/front-end/threejs-unleashed"
                  replace
                />
              }
            />

            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {!isMobile && <CustomCursor />}
      </Router>
      <Analytics />
    </HelmetProvider>
  );
};

export default App;
